import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import deburr from "lodash.deburr";
import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { ChevronUpIcon } from "@jti/brand-icons/chevron-up";
import { Container } from "../../eva-components/Container";
import { useCurrentBreakpoint } from "../../hooks/useCurrentBreakpoint";
import { usePortal } from "../../hooks/usePortal";
import styles from "./AnchorNavigation.module.scss";
export const createHash = (el) => {
    let text = el.textContent ?? "";
    text = text.trim();
    text = text.toLowerCase();
    text = deburr(text); // Remove accents
    text = text.replace(/\s+/g, "-"); // Replace spaces with hyphens
    text = text.replace(/[^\w-]+/g, ""); // Remove all non-word characters
    return text;
};
const isHTMLElement = (node) => {
    return node instanceof HTMLElement;
};
const Navigation = ({ contentElement, targetSelector }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeTargetIndex, setActiveTargetIndex] = useState(0);
    const [targets, setTargets] = useState([]);
    useEffect(() => {
        if (contentElement === null) {
            setTargets([]);
            return;
        }
        const targets = Array.from(contentElement.querySelectorAll(targetSelector))
            .filter(isHTMLElement)
            .filter((el) => el.textContent?.trim());
        setTargets(targets);
    }, [contentElement, targetSelector, setTargets]);
    const changeActiveTarget = useCallback((target) => {
        const index = targets.indexOf(target);
        setActiveTargetIndex(index);
        setIsExpanded(false);
        target.scrollIntoView({ behavior: "smooth" });
    }, [targets, setActiveTargetIndex]);
    useEffect(() => {
        if (!window.location.hash || window.location.hash === "#") {
            return;
        }
        const target = targets.find((el) => createHash(el) === window.location.hash.slice(1));
        if (target) {
            changeActiveTarget(target);
        }
    }, [targets, changeActiveTarget]);
    const handleAnchorClick = (target) => {
        changeActiveTarget(target);
        window.location.hash = createHash(target);
    };
    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };
    const activeTargetText = targets[activeTargetIndex]?.textContent ?? "";
    if (!targets.length) {
        return null;
    }
    return (_jsxs("nav", { className: styles.nav, children: [targets.length > 0 && (_jsx("button", { type: "button", className: styles.toggleButton, onClick: handleToggle, children: _jsx(Container, { children: _jsxs("span", { className: styles.toggleButtonInner, children: [_jsx("span", { className: styles.label, children: activeTargetText }), isExpanded ? (_jsx(ChevronUpIcon, { width: "20px", height: "20px" })) : (_jsx(ChevronDownIcon, { width: "20px", height: "20px" }))] }) }) })), _jsx("ul", { className: classNames(styles.list, {
                    [styles.expanded]: isExpanded,
                }), children: targets.map((target, index) => (_jsx("li", { className: classNames(styles.item), children: _jsx("a", { className: styles.link, href: `#${createHash(target)}`, onClick: (ev) => {
                            ev.preventDefault();
                            handleAnchorClick(target);
                        }, children: _jsx(Container, { children: target.textContent }) }) }, index))) })] }));
};
export const AnchorNavigation = (props) => {
    const { mainBreakpoint } = useCurrentBreakpoint();
    const isMobile = useMemo(() => ["xs", "sm"].includes(mainBreakpoint), [mainBreakpoint]);
    const mobilePortalContainer = usePortal("header");
    return isMobile && mobilePortalContainer ? (createPortal(_jsx(Navigation, { ...props }), mobilePortalContainer)) : (_jsx(Navigation, { ...props }));
};
//# sourceMappingURL=AnchorNavigation.js.map